/**
 * Generate a URL-friendly slug from a name/title with the entity ID appended.
 * Format: "my-campaign-title--cmn8n4c9x0001j1zax8s8pi7l"
 */
export function toSlug(name: string, id: string): string {
  const slug = name
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 60);
  return slug ? `${slug}--${id}` : id;
}

/**
 * Extract the entity ID from a slug-based URL parameter.
 * Handles both "my-campaign--cmn8..." and plain "cmn8..." formats.
 */
export function fromSlug(slugOrId: string): string {
  const idx = slugOrId.lastIndexOf('--');
  return idx >= 0 ? slugOrId.slice(idx + 2) : slugOrId;
}

/** Decode `:id` from react-router for entities that still use name--id URLs. */
export function jobRouteParam(raw: string | undefined): string {
  return raw ? decodeURIComponent(raw) : '';
}

/**
 * Path segment for /jobs/:id — prefers stable stored slug; falls back to legacy title--id.
 */
export function jobUrlSegment(job: { id: string; title: string; urlSlug?: string | null }): string {
  if (job.urlSlug) return job.urlSlug;
  return toSlug(job.title, job.id);
}

/**
 * Path segment for `/agency/kols/:segment`. Prefers DB `urlSlug`; falls back to legacy `name--id`.
 */
export function kolUrlSegment(kol: { id: string; name: string; urlSlug?: string | null }): string {
  if (kol.urlSlug) return kol.urlSlug;
  return toSlug(kol.name, kol.id);
}

/**
 * Path segment for `/kol/invitations/:segment`.
 * Prefers server-issued `urlSlug` (no `--cuid` suffix); falls back to legacy `title--id`.
 */
export function invitationUrlSegment(inv: {
  id: string;
  urlSlug?: string | null;
  job?: { title: string; urlSlug?: string | null };
}): string {
  if (inv.urlSlug && inv.urlSlug.trim()) return inv.urlSlug.trim();
  return toSlug(inv.job?.title || 'invitation', inv.id);
}

// Phase 2: invitations/scripts may still use toSlug(title, id). Brand org routes may stay id-based until org slugs exist.
